import { useEffect } from "react";

/**
 * Lightweight SEO controller — updates document title, meta tags, OG, Twitter,
 * canonical and arbitrary JSON-LD blocks for each route.
 *
 * Supports:
 *  - Standard meta (description, keywords)
 *  - Open Graph (og:*) for Facebook/LinkedIn previews
 *  - Twitter Card (large image) for Twitter/X previews
 *  - Canonical link
 *  - One or many JSON-LD schema blocks (Organization, Product, FAQPage,
 *    BreadcrumbList, Article, ...). These are critical for SEO and GEO
 *    (Generative Engine Optimization: ChatGPT, Perplexity, Claude, Gemini).
 */
function upsertMeta(attr, name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const JSONLD_ATTR = "data-seo-jsonld";

function syncJsonLd(blocks) {
  // Remove previous route-scoped blocks
  document.head.querySelectorAll(`script[${JSONLD_ATTR}]`).forEach((s) => s.remove());
  if (!blocks) return;
  const arr = Array.isArray(blocks) ? blocks : [blocks];
  arr.filter(Boolean).forEach((data) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute(JSONLD_ATTR, "1");
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
  });
}

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  jsonLd,
  twitterCard = "summary_large_image",
}) {
  useEffect(() => {
    if (title) document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", keywords);

    // Open Graph
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:locale", "es_ES");
    upsertMeta("property", "og:site_name", "Asirox");
    if (ogImage) upsertMeta("property", "og:image", ogImage);

    // Twitter Card
    upsertMeta("name", "twitter:card", twitterCard);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    if (ogImage) upsertMeta("name", "twitter:image", ogImage);

    // Crawler hints (allow AI bots — also covered by robots.txt)
    upsertMeta("name", "robots", "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1");

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const can = canonical || `${origin}${path}`;
    upsertLink("canonical", can);
    upsertMeta("property", "og:url", can);

    syncJsonLd(jsonLd);
  }, [title, description, keywords, canonical, ogImage, jsonLd, twitterCard]);

  return null;
}
